// src/pages/PromotionsList.jsx
import React, { useEffect, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { getAllPromos, updatePromo } from '../Services/Api';
import { useNavigate } from 'react-router-dom';

export default function PromotionsList() {
  const [promos, setPromos] = useState([]);
  const [displayDialog, setDisplayDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState('view');    // 'view' or 'edit'
  const [selectedPromo, setSelectedPromo] = useState(null);
  const [editStatus, setEditStatus] = useState('');
  const [showUserInput, setShowUserInput] = useState(false);
  const [newUserIds, setNewUserIds] = useState('');
  const navigate = useNavigate();

  const statusOptions = [
    { label: 'Active', value: true },
    { label: 'Inactive', value: false }
  ];

  // load list
  const loadPromos = async () => {
    try {
      const res = await getAllPromos();
      setPromos(res.data.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    loadPromos();
  }, []);

  // open dialog
  const openDialog = (promo, mode) => {
    setSelectedPromo(promo);
    setDialogMode(mode);
    if (mode === 'edit') {
      setEditStatus(promo.status || 'Active');
    }
    setDisplayDialog(true);
    setShowUserInput(false);
    setNewUserIds('');
  };

  const hideDialog = () => {
    setDisplayDialog(false);
    setSelectedPromo(null);
    setShowUserInput(false);
    setNewUserIds('');
  };

  // Add comma-separated user IDs
  const addUserIds = () => {
    if (!newUserIds.trim()) return;

    const idsToAdd = newUserIds.split(',').map(id => id.trim()).filter(id => id);
    if (idsToAdd.length === 0) return;

    const userType = selectedPromo.users.userTypes[0]; // e.g. 'patient'
    const existingIds = selectedPromo.users[userType] || [];

    // Filter out duplicates
    const uniqueNewIds = idsToAdd.filter(id => !existingIds.includes(id));

    if (uniqueNewIds.length > 0) {
      const updated = {
        ...selectedPromo.users,
        [userType]: [...existingIds, ...uniqueNewIds]
      };
      setSelectedPromo({ ...selectedPromo, users: updated });
    }

    setNewUserIds('');
    setShowUserInput(false);
  };

  // Remove specific user ID
  const removeUserId = (userType, idToRemove) => {
    const updated = {
      ...selectedPromo.users,
      [userType]: selectedPromo.users[userType].filter(id => id !== idToRemove)
    };
    setSelectedPromo({ ...selectedPromo, users: updated });
  };
  const couponValueTemplate = row => {
    const value = row.discount?.range?.[0]?.discountValue;
    const type = row.discount?.discountType?.toLowerCase();

    if (value == null || isNaN(value)) return '—';

    return type === 'flat' ? `₹${value}` : `${value}%`;
  };


  // build business-lines array
  const businessEntries = [];
  const lines = selectedPromo?.businessLines?.lines || [];

  if (lines.includes('medicines')) {
    const m = selectedPromo.businessLines.medicines;
    businessEntries.push({
      line: 'Medicines',
      level: m.level || '—',
      category: (m.category || []).join(', ') || '—',
      items: (m.skus || []).join(', ') || '—'
    });
  }

  if (lines.includes('diagnostics')) {
    const d = selectedPromo.businessLines.diagnostics;
    businessEntries.push({
      line: 'Diagnostics',
      level: d.level || '—',
      category: (d.category || []).join(', ') || '—',
      items: (d.tests || []).join(', ') || '—'
    });
  }

  const userEntries = selectedPromo?.users
    ? selectedPromo.users.userTypes.map(type => ({
      type,
      values: selectedPromo.users[type] || [],
      isUserSpecific: selectedPromo.couponType === 'userspecific'
    }))
    : [];

  // build region-details array dynamically
  const regionEntries = [];
  if (selectedPromo?.regions) {
    const { zones, circles, clusters, clinics } = selectedPromo.regions;
    if (zones?.length) regionEntries.push({ region: 'Zones', values: zones.join(', ') });
    if (circles?.length) regionEntries.push({ region: 'Circles', values: circles.join(', ') });
    if (clusters?.length) regionEntries.push({ region: 'Clusters', values: clusters.join(', ') });
    if (clinics?.length) regionEntries.push({ region: 'Clinics', values: clinics.join(', ') });
  }

  // save back
  const handleSave = async () => {
    try {
      await updatePromo(selectedPromo.id, {
        ...selectedPromo,
        status: editStatus
      });
      await loadPromos();
      hideDialog();
    } catch (err) {
      console.error(err);
    }
  };

  // Custom template for user values with remove functionality
  const userValuesTemplate = (rowData) => {
    if (!rowData.isUserSpecific || dialogMode !== 'edit') {
      return Array.isArray(rowData.values) && rowData.values.length
        ? rowData.values.join(', ')
        : '—';
    }

    if (!Array.isArray(rowData.values) || rowData.values.length === 0) {
      return '—';
    }

    return (
      <div className="flex flex-wrap gap-1">
        {rowData.values.map((id, idx) => (
          <span key={idx} className="inline-flex items-center bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
            {id}
            <button
              onClick={() => removeUserId(rowData.type, id)}
              className="ml-1 text-red-600 hover:text-red-800"
              title="Remove"
            >
              ×
            </button>
          </span>
        ))}
      </div>
    );
  };

  return (
    <div className="p-6 rounded-lg shadow bg-white">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-semibold">Promo List</h3>
        <Button
          label="Create Promo"
          icon="pi pi-plus"
          className="p-button-sm"
          onClick={() => navigate('/create')}
        />
      </div>

      <DataTable value={promos} paginator rows={10} className="p-datatable-sm">
        <Column field="couponCode" header="Code" />
        <Column field="couponName" header="Name" />
        <Column field="status" header="Status" />
        <Column field="startDate" header="Start" />
        <Column field="endDate" header="End" />
        <Column field="couponType" header="Type" />
        <Column body={couponValueTemplate} header="Value" />
        <Column
          body={row => (
            <div className="flex space-x-2">
              <Button
                icon="pi pi-eye"
                className="p-button-rounded p-button-text p-button-sm"
                onClick={() => openDialog(row, 'view')}
              />
              <Button
                icon="pi pi-pencil"
                className="p-button-rounded p-button-text p-button-sm"
                onClick={() => openDialog(row, 'edit')}
              />
            </div>
          )}
          header="Actions"
        />
      </DataTable>

      <Dialog
        header={dialogMode === 'view' ? 'View Promo' : 'Edit Promo'}
        visible={displayDialog}
        style={{ width: '800px' }}
        modal
        onHide={hideDialog}
        footer={
          dialogMode === 'edit' && (
            <div className="flex justify-end space-x-2">
              <Button
                label="Cancel"
                icon="pi pi-times"
                className="p-button-text"
                onClick={hideDialog}
              />
              <Button
                label="Save"
                icon="pi pi-check"
                onClick={handleSave}
              />
            </div>
          )
        }
      >
        {selectedPromo && (
          <>
            {/* two-column Tailwind grid */}
            <div className="grid grid-cols-2 gap-4 p-4">
              {[
                ['Coupon Code', selectedPromo?.couponCode],
                ['Coupon Name', selectedPromo?.couponName],
                ['Type', selectedPromo?.couponType],
                ['Description', selectedPromo?.description || '—'],
                ['Membership?', selectedPromo?.applyOnMembership ? 'Yes' : 'No'],
                ['Start Date', selectedPromo?.startDate],
                ['End Date', selectedPromo?.endDate],
                ['Discount Type', selectedPromo?.discount.discountType],
                ['Discount Value', couponValueTemplate(selectedPromo)]
              ].map(([label, val]) => (
                <div key={label} className="flex">
                  <span className="font-semibold mr-2">{label}:</span>
                  <span>{val}</span>
                </div>
              ))}

              {/* status dropdown in edit mode */}
              {dialogMode === 'edit' && (
                <div className="flex col-span-2 items-center">
                  <span className="font-semibold mr-2">Status:</span>
                  {selectedPromo.status === false ? (
                    <span className="text-gray-700">Inactive</span>
                  ) : (
                    <Dropdown
                      value={editStatus}
                      options={statusOptions}
                      onChange={e => setEditStatus(e.value)}
                      placeholder="Select status"
                      className='border border-gray'
                    />
                  )}
                </div>
              )}
            </div>

            {/* business-lines table */}
            <div className="mt-6 px-4">
              <h5 className="mb-2 text-lg font-medium">Business Lines</h5>
              <DataTable
                value={businessEntries}
                className="p-datatable-gridlines"
                stripedRows
                responsiveLayout="stack"
              >
                <Column field="line" header="Line" />
                <Column field="level" header="Level" />
                <Column field="category" header="Category" />
                <Column field="items" header="Items" />
              </DataTable>
            </div>

            {/* User Details */}
            <div className="mt-6 px-4">
              <div className="flex items-center justify-between mb-2">
                <h5 className="text-lg font-medium">User Details</h5>
                {dialogMode === 'edit' && selectedPromo.couponType === 'userspecific' && (
                  <Button
                    label="Add User IDs"
                    icon="pi pi-plus"
                    className="p-button-sm p-button-outlined"
                    onClick={() => setShowUserInput(!showUserInput)}
                  />
                )}
              </div>

              {/* Input field for adding comma-separated user IDs */}
              {showUserInput && dialogMode === 'edit' && selectedPromo.couponType === 'userspecific' && (
                <div className="mb-4 p-3 border border-gray-200 rounded-lg bg-gray-50">
                  <label className="block text-sm font-medium mb-2">
                    Add {selectedPromo.users.userTypes[0]} IDs (comma-separated):
                  </label>
                  <div className="flex space-x-2">
                    <InputText
                      value={newUserIds}
                      onChange={(e) => setNewUserIds(e.target.value)}
                      placeholder="e.g., 123, 456, 789"
                      className="flex-1"
                    />
                    <Button
                      label="Add"
                      icon="pi pi-check"
                      onClick={addUserIds}
                      disabled={!newUserIds.trim()}
                      className="p-button-sm"
                    />
                    <Button
                      label="Cancel"
                      icon="pi pi-times"
                      onClick={() => {
                        setShowUserInput(false);
                        setNewUserIds('');
                      }}
                      className="p-button-sm p-button-outlined"
                    />
                  </div>
                </div>
              )}

              <DataTable
                value={userEntries}
                className="p-datatable-sm p-datatable-gridlines"
                stripedRows
                responsiveLayout="stack"
              >
                <Column field="type" header="User Type" />
                <Column body={userValuesTemplate} header="IDs / Values" />
              </DataTable>
            </div>

            {/* Region Details */}
            {regionEntries.length > 0 && (
              <div className="mt-6 px-4">
                <h5 className="mb-2 text-lg font-medium">Region Details</h5>
                <DataTable
                  value={regionEntries}
                  className="p-datatable-sm p-datatable-gridlines"
                  stripedRows
                  responsiveLayout="stack"
                >
                  <Column field="region" header="Region" />
                  <Column field="values" header="Selections" />
                </DataTable>
              </div>
            )}
          </>
        )}
      </Dialog>
    </div>
  );
}